import type { PickSampleExt, View } from "@novorender/api";
import type { ReadonlyVec2, vec3 } from "gl-matrix";
import { vec2 } from "gl-matrix";

import { POINT_LINE_MARKER_FILL_COLOR, POINT_LINE_MARKER_STROKE_COLOR } from "features/pointLine/constants";
import { createSectionPlaneCursorSvg } from "features/sectionPlane";
import { radToDeg } from "utils/math";
import { isRealVec } from "utils/misc";

import type { SurfaceProjectionSize } from "./surfaceProjection";
import { getSurfaceProjection, getSurfaceProjectionTransform } from "./surfaceProjection";
export { getSurfaceProjectionTransform } from "./surfaceProjection";

export function moveSvgCursor({
    svg,
    view,
    size,
    x,
    y,
    pickResult,
    color,
    overrideKind = undefined,
    canvas = null,
}: {
    svg: SVGSVGElement;
    view: View;
    size: SurfaceProjectionSize;
    x: number;
    y: number;
    pickResult: PickSampleExt | undefined;
    color: string;
    overrideKind:
        | "edge"
        | "corner"
        | "surface"
        | "cross"
        | "rotatedCross"
        | "coordinatesSurface"
        | "sectionPlane"
        | undefined;
    pointSnapGuides?: (ReadonlyVec2 | undefined)[];
    canvas?: HTMLCanvasElement | null;
}) {
    if (!svg || !view) {
        return;
    }
    const g = svg.children.namedItem("cursor");
    if (!g) {
        return;
    }
    if (x < 0) {
        g.innerHTML = "";
        return;
    }
    if (overrideKind === "cross") {
        g.innerHTML = `<path d="M-10,0L10,0M0,-10L0,10" stroke-width="2" stroke-linecap="round" stroke="${
            color || "red"
        }"/>`;
    } else if (overrideKind === "rotatedCross") {
        if (color === "none") {
            g.innerHTML = "";
        } else {
            g.innerHTML = `<g transform="translate(-12 -12)">
                <path d="M6 6L18 18" stroke="#E05252" stroke-width="5" stroke-linecap="round"/>
                <path d="M18 6L6 18" stroke="#E05252" stroke-width="5" stroke-linecap="round"/>
                <path d="M6 6L18 18" stroke="#570F0F" stroke-width="2" stroke-linecap="round"/>
                <path d="M18 6L6 18" stroke="#570F0F" stroke-width="2" stroke-linecap="round"/>
            </g>`;
        }
        if (canvas) {
            canvas.style.cursor = "none";
        }
    } else if (overrideKind === "sectionPlane") {
        g.innerHTML = createSectionPlaneCursorSvg(pickResult, x, y, size, view);
        if (canvas) {
            canvas.style.cursor = "none";
        }
    } else {
        const kind = (overrideKind ? overrideKind : pickResult?.sampleType) ?? "corner";
        const isNormalValid = pickResult?.normalVS && isRealVec(pickResult.normalVS);
        if ((kind === "surface" || kind === "coordinatesSurface") && isNormalValid) {
            const surfaceProjection = getSurfaceProjection(pickResult.normalVS, x, y, size, view);
            if (surfaceProjection?.isTilted) {
                const style = surfaceProjection.transform ? `style="transform:${surfaceProjection.transform}"` : "";
                g.innerHTML = createFeatureSvg({
                    kind,
                    style,
                    normal: surfaceProjection.normal,
                    color,
                });
            } else {
                g.innerHTML = createFeatureSvg({
                    kind,
                    color,
                });
            }
        } else {
            if (kind === "edge") {
                g.innerHTML = `<path d="M-12,0L12,0M0,-7L0,7" stroke-width="2" stroke-linecap="round" stroke="${
                    color || "red"
                }"/>`;
            } else {
                g.innerHTML = `<path d="M-10,-10L10,10M-10,10L10,-10" stroke-width="2" stroke-linecap="round" stroke="${
                    color || "red"
                }"/>`;
            }
        }
        if (canvas) {
            canvas.style.cursor = "none";
        }
    }

    g.setAttribute("transform", `translate(${x},${y})`);
}

export function moveSvgCursorPointSnap({
    svg,
    view,
    x,
    y,
    pointSnapGuides,
    snap,
    canvas = null,
    color = POINT_LINE_MARKER_STROKE_COLOR,
}: {
    svg: SVGSVGElement;
    view: View;
    x: number;
    y: number;
    pointSnapGuides?: (ReadonlyVec2 | undefined)[];
    snap?: boolean;
    canvas?: HTMLCanvasElement | null;
    color?: string;
}) {
    if (!svg || !view) {
        return;
    }
    const g = svg.children.namedItem("cursor");
    if (!g) {
        return;
    }
    let paths: string;
    if (pointSnapGuides?.length) {
        const offset = vec2.create();
        paths = pointSnapGuides
            .map((point) => {
                if (!point) {
                    return "";
                }
                vec2.scale(offset, point, 9);
                return `
                    <path d="M12 12l${offset[0]} ${offset[1]}" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
                    <path d="M12 12l${offset[0]} ${offset[1]}" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
                `;
            })
            .join("");
    } else {
        paths = `
            <path d="M12 3V21" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
            <path d="M12 3V21" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
            <path d="M21 12L3 12" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
            <path d="M21 12L3 12" stroke="${color}" stroke-width="2" stroke-linecap="round"/>
        `;
    }
    const r = snap ? 6 : 3;
    g.innerHTML = `
        <g transform="translate(-12,-12)">
            ${paths}
            <circle cx="12" cy="12" r="${r}" fill="${color}"/>
            <circle cx="12" cy="12" r="${r}" fill="${POINT_LINE_MARKER_FILL_COLOR}" stroke="${color}" stroke-width="2"/>
        </g>
    `;
    if (canvas) {
        canvas.style.cursor = "none";
    }

    g.setAttribute("transform", `translate(${x},${y})`);
}

export function moveSvgCursorDimension({
    svg,
    view,
    x,
    y,
    snap,
    normalVS,
    size,
    isSelected = false,
    snapToSurface = true,
    canvas = null,
}: {
    svg: SVGSVGElement;
    view: View;
    x: number;
    y: number;
    snap?: boolean;
    normalVS?: vec3;
    size?: { width: number; height: number };
    isSelected?: boolean;
    snapToSurface?: boolean;
    canvas?: HTMLCanvasElement | null;
}) {
    if (!svg || !view) {
        return;
    }
    const g = svg.children.namedItem("cursor");
    if (!g) {
        return;
    }

    if (snap && isSelected) {
        const transformStyle =
            snapToSurface && normalVS && size && isRealVec(normalVS)
                ? getSurfaceProjectionTransform(normalVS, x, y, size, view)
                : "";
        const style = transformStyle ? `style="transform:${transformStyle}"` : "";

        g.innerHTML = `
            <g transform="translate(-12,-12)" ${style}>
                <rect width="20" height="20" x="2" y="2" fill="#232729" opacity="0.5" rx="4"/>
                <rect width="6" height="6" x="9" y="9" fill="#0f3" rx="2"/>
            </g>
        `;
    } else {
        g.innerHTML = `
            <g transform="translate(-12,-12)">
                <path d="M12 3V21" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
                <path d="M12 3V21" stroke="#0F3" stroke-width="2" stroke-linecap="round"/>
                <path d="M21 12L3 12" stroke="black" stroke-opacity="0.38" stroke-width="5" stroke-linecap="round"/>
                <path d="M21 12L3 12" stroke="#0F3" stroke-width="2" stroke-linecap="round"/>
                <circle cx="12" cy="12" r="3" fill="${POINT_LINE_MARKER_STROKE_COLOR}"/>
                <circle cx="12" cy="12" r="3" fill="${POINT_LINE_MARKER_FILL_COLOR}" stroke="${POINT_LINE_MARKER_STROKE_COLOR}" stroke-width="2"/>
            </g>
        `;
    }

    if (canvas) {
        canvas.style.cursor = "none";
    }

    g.setAttribute("transform", `translate(${x},${y})`);
}

/**
 * @returns false if cursor should be reset to default
 */
export function moveSvgCameraCursor({
    svg,
    view,
    x,
    y,
    canvas,
    buttons = 0,
    isMouse = true,
    orbitOnPrimaryButton = false,
    enableOrthoOrbitCursor = true,
    spacebarHeld = false,
    spacebarPanning = false,
}: {
    svg: SVGSVGElement;
    view: View;
    x: number;
    y: number;
    canvas: HTMLCanvasElement | null;
    buttons?: number;
    isMouse?: boolean;
    orbitOnPrimaryButton?: boolean;
    enableOrthoOrbitCursor?: boolean;
    spacebarHeld?: boolean;
    spacebarPanning?: boolean;
}) {
    const g = svg.children.namedItem("camera-cursor");
    if (!g) {
        return false;
    }

    if (x === -100) {
        g.innerHTML = "";
        return false;
    }

    if (view.activeController.kind === "hover" && view.controllers.hover.clickPosition) {
        const [cx, cy] = view.controllers.hover.clickPosition;
        const angle = radToDeg(Math.atan2(cy - y, cx - x)) + -90;
        const pivot = `
            <circle r="10" cx="${cx}" cy="${cy}" fill="black" stroke="white" />
            <circle r="5" cx="${cx}" cy="${cy}" stroke="white" />
        `;
        const arrow = isMouse
            ? `<path d="M-6 0h12L0 -8z" transform="translate(${x},${y}) rotate(${angle})" stroke="white" />`
            : "";
        g.innerHTML = `${pivot}${arrow}`;
        if (canvas) {
            canvas.style.cursor = "none";
        }
        return true;
    }

    const kind = view.activeController.kind;
    const isOrthoOrbitDrag = enableOrthoOrbitCursor && kind === "ortho" && buttons === (orbitOnPrimaryButton ? 1 : 2);

    // Spacebar pan: if actively panning, the hook owns the cursor.
    // For any other drag while spacebar is held (e.g. RMB orbit), clear SVG and fall through.
    if (spacebarHeld) {
        if (spacebarPanning) {
            return true;
        }
        g.innerHTML = "";
    }

    // Default mode (flight): left-click drag → grabbing hand cursor
    if (kind === "flight" && buttons === 1) {
        g.innerHTML = "";
        if (canvas) {
            canvas.style.cursor = "grabbing";
        }
        return true;
    }

    // Middle-click drag in any non-hover mode → grabbing hand cursor (matches spacebar pan)
    if (buttons === 4) {
        g.innerHTML = "";
        if (canvas) {
            canvas.style.cursor = "grabbing";
        }
        return true;
    }

    if (
        ((kind === "flight" || kind === "hover") && buttons === 2) ||
        ((kind === "cadRightPan" || kind === "cadMiddlePan") && buttons === 1) ||
        isOrthoOrbitDrag
    ) {
        const pivot = kind === "ortho" ? undefined : view.controllers[kind].pivot;
        if (pivot || kind === "ortho") {
            const point = pivot ? view.convert.worldSpaceToScreenSpace([pivot.center], { round: false })[0] : undefined;
            const pivotIcon = point ? `<circle r="3" cx="${point[0]}" cy="${point[1]}" stroke="white" />` : "";
            const cursorIcon = isMouse
                ? `<g transform="translate(${x - 12},${y - 12})">
                    <path d='M5.97683 16.6239L6.64016 17.2873C9.57094 20.218 14.3227 20.218 17.2534 17.2873C19.2263 15.3144 19.8711 12.5164 19.1879 10.0042M5.93854 19.4267V16.5983H8.76696' stroke='white'/>
                    <path d='M17.917 7.34013L17.2537 6.6768C14.3229 3.74602 9.57116 3.74602 6.64038 6.6768C4.65583 8.66135 4.0151 11.4808 4.71818 14.0045M17.9553 4.53732L17.9553 7.36575L15.1269 7.36575' stroke='white'/>
                    <circle r='3' cx='12' cy='12' fill='black' stroke='white' />
                </g>`
                : "";
            g.innerHTML = `${pivotIcon}${cursorIcon}`;
        }

        if (canvas) {
            canvas.style.cursor = "none";
        }
        return true;
    }

    g.innerHTML = "";
    return false;
}

function createFeatureSvg({
    kind,
    style,
    normal,
    color,
}: {
    kind: "edge" | "corner" | "surface" | "coordinatesSurface";
    style?: string;
    normal?: vec3;
    color?: string;
}) {
    switch (kind) {
        case "surface":
            return surfaceSvg(style, normal, color);
        case "coordinatesSurface":
            return coordinatesSurfaceSvg(style);
        default:
            return "";
    }
}

function coordinatesSurfaceSvg(style: string | undefined): string {
    return `<circle r="15" stroke="#00FF33" stroke-width="2" fill="none" ${style}/>
            <circle r="10" fill="#000000ff" ${style}/>
            <circle r="2" fill="#00FF33" ${style}/>`;
}

function surfaceSvg(style: string | undefined, normal: number[] | undefined, color: string | undefined): string {
    if (normal === undefined) {
        return `<circle r="20" fill="rgba(255,255,255,0.25)" /><circle r="2" fill=${color} />`;
    } else {
        return `<circle r="20" fill="rgba(255,255,255,0.25)" ${style}/><line x2="${(normal[0] * 20).toFixed(1)}" y2="${(
            normal[1] * -20
        ).toFixed(1)}" stroke=${color} stroke-width="2" stroke-linecap="round" />`;
    }
}

export function getLabelPixelWidth(
    label: string,
    fontSize: number = 12,
    padding: number = 20,
    fontPixelSizeFactor: number = 1,
) {
    // Estimate: monospace font, each character ~0.6 * fontSize px wide
    const charWidth = fontSize * fontPixelSizeFactor;
    return Math.ceil(label.length * charWidth + padding);
}

export function getRoundedRectPath(width: number, height: number, radius: number) {
    return `M0 ${radius}C0 ${radius / 2} ${radius / 2} 0 ${radius} 0H${width - radius}C${width - radius / 2} 0 ${width} ${radius / 2} ${width} ${radius}V${height - radius}C${width} ${height - radius / 2} ${width - radius / 2} ${height} ${width - radius} ${height}H${radius}C${radius / 2} ${height} 0 ${height - radius / 2} 0 ${height - radius}V${radius}Z`;
}
